import './excess-tower-layer-detail.scss';

import {
  Box,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Circle, DeleteOutlined, Sync } from '@mui/icons-material';
import {
  ConvergeTooltip,
  NewConvergeButton,
} from '../../../../presentation/components';
import {
  IUnderwritingQuoteExcessTowerLayer,
  NoteTargetName,
  NoteTargetType,
  useDeleteExcessTowerLayer,
} from '@converge/graphql';
import { RaterNotesButton, RaterNotesRow } from '../common';
import { asCase, formatDollarAmount, formatPercent } from '@converge/utils';
import { useEffect, useMemo } from 'react';
import {
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from '../data';

import Add from '@mui/icons-material/Add';
import { CELL_COUNT } from './rater.excess.view';
import { CONVERGE_UUID, numberFieldMaxValue } from '@converge/data';
import { CurrencyOrNotApplicable } from './currency-or-not-applicable';
import { InsurerSelectBox } from './insurer-select-box';
import { NumberField } from '../common/number.field';
import { PercentOrNotApplicable } from './percent-or-not-applicable';
import { RaterExcessTowerLayerParticipant } from './excess-tower-layer-participant-detail';
import { RaterTableExcessTitleCell } from './title-cell';
import { getParsedValue } from '../coverage-rating';
import { hasErrors } from '../enterprise-rating';
import { useConvergeSnackBar } from '../../../../contexts';
import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

export const RaterExcessTowerLayer = ({
  layer,
  layer: { participants, uuid, boundPremium, limit },
  isDisabled,
  isLastLayer,
}: {
  layer: IUnderwritingQuoteExcessTowerLayer;
  isDisabled: boolean;
  isLastLayer: boolean;
}) => {
  const { notify } = useConvergeSnackBar();

  const isPrimaryLayer = layer.position === 0 || !!layer.primary;

  const { dispatch, actions } = useRaterActionDispatchContext();

  const excessTowerLayerDeleter = useDeleteExcessTowerLayer();

  const { isEditable, quote } = useRaterStateContextSelector(
    ({ isEditable, quote }) => {
      return {
        isEditable,
        quote,
      };
    }
  );

  useEffect(() => {
    if (excessTowerLayerDeleter.error) {
      notify({
        message: `Failed to delete layer: ${excessTowerLayerDeleter.error}`,
        variant: 'error',
        preventDuplicate: true,
      });
    }
  }, [excessTowerLayerDeleter.error, notify]);

  const excludeFromInsurerSelect = useMemo(() => {
    const exclude: string[] = [];

    for (const layer of quote.excessTowerLayers) {
      for (const participant of layer.participants) {
        exclude.push(participant.uuid);
      }
    }

    if (isPrimaryLayer) {
      exclude.push(CONVERGE_UUID);
    }

    return exclude;
  }, [isPrimaryLayer, quote.excessTowerLayers]);

  const handleRemoveLayer = () => {
    if (layer.isNew) {
      dispatch(actions.removeExcessLayer(layer.uuid));
      return;
    }

    excessTowerLayerDeleter
      .deleteExcessTowerLayer({
        variables: {
          position: layer.position ?? -1,
          quoteUuid: quote.uuid,
        },
      })
      .then(() => {
        dispatch(actions.removeExcessLayer(layer.uuid));
      });
  };

  const {
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const hasInvalidSelectedPremium = hasErrors(`${uuid}:boundPremium`, errors);

  useEffect(() => {
    dispatch(actions.setIsInvalidRaterData(hasInvalidSelectedPremium));
  }, [actions, dispatch, hasInvalidSelectedPremium]);

  useEffect(() => {
    if (boundPremium === 0) {
      setError(`${uuid}:boundPremium`, {
        type: `${uuid}:boundPremium`,
        message: `Premium must be a greater than 0`,
      });
    }
  }, [boundPremium, setError, uuid]);

  const handleUpdatePremium = (newValue: number | null) => {
    const parsedValue = getParsedValue(newValue);
    if (!parsedValue) {
      setError(`${uuid}:boundPremium`, {
        type: `${uuid}:boundPremium`,
        message: `Premium must be a greater than 0`,
      });
      return;
    }
    dispatch(
      actions.updateExcessTowerLayer(layer.uuid, {
        boundPremium: newValue ?? null,
      })
    );
    clearErrors(`${uuid}:boundPremium`);
  };

  const renderLayerFields = () => (
    <TableRow
      sx={{
        backgroundColor: hasInvalidSelectedPremium
          ? 'rgba(219, 82, 76, 0.1)'
          : '#576082',
        fontSize: '14px',
      }}
    >
      <RaterTableExcessTitleCell columnCount={1}>
        <RaterTableExcessTitleCell.Typography
          isError={hasInvalidSelectedPremium}
        >
          {'name' in layer
            ? layer.name
            : isPrimaryLayer
            ? 'Primary Layer'
            : 'Excess Layer'}
          {(layer.isNew || layer.hasChanged) && (
            <Circle
              color={layer.isNew ? 'success' : 'warning'}
              sx={{
                marginLeft: 1,
                fontSize: '8px',
              }}
            />
          )}
        </RaterTableExcessTitleCell.Typography>
      </RaterTableExcessTitleCell>

      <RaterTableExcessTitleCell columnCount={1} />

      <RaterTableExcessTitleCell columnCount={1}>
        <RaterTableExcessTitleCell.Typography
          isError={hasInvalidSelectedPremium}
        >
          {formatDollarAmount(limit)}
        </RaterTableExcessTitleCell.Typography>
      </RaterTableExcessTitleCell>

      <RaterTableExcessTitleCell
        isError={hasInvalidSelectedPremium}
        columnCount={1}
      >
        {typeof layer.companyShare === 'number'
          ? formatPercent((layer.companyShare ?? 0) * 100)
          : '0'}
      </RaterTableExcessTitleCell>

      <RaterTableExcessTitleCell columnCount={1}>
        <RaterTableExcessTitleCell.Typography
          isError={hasInvalidSelectedPremium}
        >
          <CurrencyOrNotApplicable>
            {layer.attachmentPoint}
          </CurrencyOrNotApplicable>
        </RaterTableExcessTitleCell.Typography>
      </RaterTableExcessTitleCell>

      <RaterTableExcessTitleCell columnCount={1}>
        <RaterTableExcessTitleCell.Typography
          isError={hasInvalidSelectedPremium}
        >
          <CurrencyOrNotApplicable>
            {layer.minimumPremium}
          </CurrencyOrNotApplicable>
        </RaterTableExcessTitleCell.Typography>
      </RaterTableExcessTitleCell>

      <RaterTableExcessTitleCell columnCount={1}>
        <RaterTableExcessTitleCell.Typography
          isError={hasInvalidSelectedPremium}
        >
          <CurrencyOrNotApplicable>
            {layer.benchmarkPremium}
          </CurrencyOrNotApplicable>
        </RaterTableExcessTitleCell.Typography>
      </RaterTableExcessTitleCell>

      <RaterTableExcessTitleCell
        isError={hasInvalidSelectedPremium}
        columnCount={1}
      >
        <Box minWidth={100}>
          {isPrimaryLayer ? (
            <Typography className="layer-item">
              {formatDollarAmount(boundPremium)}
            </Typography>
          ) : (
            <NumberField
              isCurrency
              name={`${uuid}:boundPremium`}
              value={boundPremium}
              max={numberFieldMaxValue}
              isDisabled={isDisabled}
              error={hasInvalidSelectedPremium}
              onBlur={handleUpdatePremium}
              hasChanged={layer.hasChanged}
            />
          )}
        </Box>
      </RaterTableExcessTitleCell>

      <RaterTableExcessTitleCell columnCount={1}>
        <RaterTableExcessTitleCell.Typography
          isError={hasInvalidSelectedPremium}
        >
          <PercentOrNotApplicable>
            {layer.boundUnderlyingPercent}
          </PercentOrNotApplicable>
        </RaterTableExcessTitleCell.Typography>
      </RaterTableExcessTitleCell>

      <RaterTableExcessTitleCell columnCount={1}>
        <RaterTableExcessTitleCell.Typography
          isError={hasInvalidSelectedPremium}
        >
          <PercentOrNotApplicable>
            {layer.boundIncreasedLimitFactor}
          </PercentOrNotApplicable>
        </RaterTableExcessTitleCell.Typography>
      </RaterTableExcessTitleCell>

      <RaterTableExcessTitleCell columnCount={1}>
        <Stack spacing={1} className="tower-delete-notes-container">
          {!isPrimaryLayer && isEditable && (
            <ConvergeTooltip title={`Remove layer`}>
              <IconButton
                className="delete-Button"
                onClick={handleRemoveLayer}
                disabled={isDisabled || excessTowerLayerDeleter.loading}
              >
                {excessTowerLayerDeleter.loading ? (
                  <Sync className="spin" />
                ) : (
                  <DeleteOutlined
                    className={`delete-icon ${
                      hasInvalidSelectedPremium ? 'color-black' : ''
                    }`}
                  />
                )}
              </IconButton>
            </ConvergeTooltip>
          )}

          <RaterNotesButton
            uuid={layer.uuid}
            color={hasInvalidSelectedPremium ? 'black' : 'white'}
            targetName={NoteTargetName.ExcessTowerLayer}
            targetType={NoteTargetType.Node}
          />
        </Stack>
      </RaterTableExcessTitleCell>
    </TableRow>
  );

  return (
    <>
      {renderLayerFields()}

      <RaterNotesRow
        uuid={layer.uuid}
        targetName={NoteTargetName.ExcessTowerLayer}
        targetType={NoteTargetType.Node}
        colSpan={CELL_COUNT}
      />

      {participants.length === 0 && (
        <TableRow>
          <TableCell colSpan={CELL_COUNT}>
            <Box
              className="no-participant-container"
              flexGrow={1}
              textAlign={'center'}
              padding={1}
            >
              No participants in {asCase(layer.name ?? 'layer', 'lower')}
            </Box>
          </TableCell>
        </TableRow>
      )}

      {participants.map((participant, index) => (
        <RaterExcessTowerLayerParticipant
          key={index}
          participant={participant}
          layer={layer}
          layerLimit={limit ?? 0}
          isDisabled={isDisabled}
          isPrimaryLayer={isPrimaryLayer}
          excludeFromInsurerSelect={excludeFromInsurerSelect}
        />
      ))}

      {isEditable &&
        (isPrimaryLayer ? isLastLayer || participants.length === 0 : true) && (
          <TableRow className="add-participant-container">
            <TableCell
              sx={{
                width: '220px',
              }}
              colSpan={3}
            >
              {(isPrimaryLayer ? participants.length === 0 : true) && (
                <Box
                  sx={{
                    maxWidth: '220px',
                  }}
                >
                  <InsurerSelectBox
                    name={`${layer.uuid}:insurerUuid:${participants.length}`}
                    placeholder={'Add participant'}
                    insurerEntity={null}
                    exclude={excludeFromInsurerSelect}
                    isDisabled={isDisabled}
                    clearOnBlur
                    onChange={(participant) => {
                      if (participant) {
                        dispatch(
                          actions.addExcessTowerLayerParticipant(layer.uuid, {
                            ...participant,
                            quote: { uuid: uuidv4(), limit: layer.limit },
                          })
                        );
                      }
                    }}
                  />
                </Box>
              )}
            </TableCell>
            <TableCell colSpan={CELL_COUNT - 3}>
              {isLastLayer && (
                <Stack flexDirection={'row'} justifyContent={'flex-end'}>
                  <Box className="button-add-option">
                    <NewConvergeButton
                      disabled={isDisabled}
                      variant="outlined"
                      label={`ADD EXCESS LAYER #${(layer.position ?? 0) + 1}`}
                      onClick={() => {
                        dispatch(actions.addExcessTowerLayer());
                      }}
                      startIcon={<Add />}
                      color="success"
                      sx={{ textTransform: 'none' }}
                    />
                  </Box>
                </Stack>
              )}
            </TableCell>
          </TableRow>
        )}
    </>
  );
};
